import { useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import { useFormContext } from 'react-hook-form'

import { currencyParser, currencyParserToNumber } from '@/shared/lib/currency-parser'
import { AppsflyerEvent, sendAppsflyer } from '@/shared/metrics'
import { Button, InputCurrency } from '@/shared/ui'

import { type TFormValues, useDriverRechargeMetricsStore, useMetrics } from '../../model'

import styles from './bottom-sheet-amount.module.scss'

type TBottomSheetAmountProps = {
    triggerClose: () => void
}

export const BottomSheetAmount = ({ triggerClose }: TBottomSheetAmountProps): React.ReactNode => {
    const {
        watch,
        setValue,
        formState: { errors },
    } = useFormContext<TFormValues>()

    const { financeTopupSumErrorEvent } = useMetrics({
        formMethods: useFormContext<TFormValues>(),
    })
    const resetSelectedTagAmount = useDriverRechargeMetricsStore(
        (state) => state.resetSelectedTagAmount,
    )

    let isError: null | string = null
    const errorValueRef = useRef<null | string>(null)
    const inputRef = useRef<HTMLInputElement>(null)
    const [currentValue, setCurrentValue] = useState(() => currencyParser(watch('value')))

    useEffect(() => {
        inputRef.current?.focus()
    }, [])

    const isValid =
        !errors.value &&
        currentValue &&
        ((): boolean => {
            if (!watch('paymentMethod')) {
                return false
            }
            const numberValue = currencyParserToNumber(currentValue)

            if (
                (currentValue.length > 1 && numberValue <= watch('paymentMethod').limits.min) ||
                numberValue >= watch('paymentMethod').limits.max
            ) {
                isError = numberValue >= watch('paymentMethod').limits.min ? 'max' : 'min'
            } else {
                isError = null
            }

            return (
                !Number.isNaN(numberValue) &&
                numberValue >= watch('paymentMethod').limits.min &&
                numberValue <= watch('paymentMethod').limits.max
            )
        })()

    const handleButtonClick = (): void => {
        if (!isValid) {
            if (errorValueRef.current !== currentValue && isError) {
                errorValueRef.current = currentValue
                sendAppsflyer(
                    AppsflyerEvent.finance_topup_sum_error,
                    financeTopupSumErrorEvent(isError),
                )
            }

            return
        }

        if (currentValue !== watch('value')) {
            resetSelectedTagAmount()
            sendAppsflyer(AppsflyerEvent.finance_topup_sum_change, {})
            setValue('value', currencyParserToNumber(currentValue).toString())
        }

        triggerClose()
    }

    return (
        <div className={styles['bottom-sheet-amount']}>
            <InputCurrency
                ref={inputRef}
                maxLength={11}
                value={currentValue}
                watch={currentValue}
                onChange={(event) => {
                    const value = event.target.value

                    if (
                        (value.includes(',') && value.split(',')[1].length > 2) ||
                        /,{2,}/.test(value) ||
                        (value.includes(',') && value.includes('.'))
                    ) {
                        return
                    }
                    setCurrentValue(value.replace('.', ','))
                }}
            />
            <span
                className={clsx(
                    styles['bottom-sheet-amount__text'],
                    !isValid && styles['bottom-sheet-amount__text_error'],
                )}
            >
                Введите от {currencyParser(watch('paymentMethod').limits.min)} до{' '}
                {currencyParser(watch('paymentMethod').limits.max)} ₽
            </span>
            <div className={styles['bottom-sheet-amount__button']}>
                <Button
                    aria-disabled={!isValid}
                    size='large'
                    variant='primary'
                    className={clsx(
                        !isValid && styles['bottom-sheet-amount__button--disabled-target'],
                    )}
                    onClick={handleButtonClick}
                >
                    Готово
                </Button>
            </div>
        </div>
    )
}
